import { Link, NavLink } from 'react-router'
import { Button } from '../platform/primitives/Button'
import { Icon } from '../platform/icons/Icon'
import { useHitTarget } from '../platform/responsive/hitTarget'
import { NAV_FOOT, NAV_GROUPS, withBadge } from './nav'
import type { NavBadges, NavItem } from './nav'
import './shell.css'

/**
 * The grouped sidebar, collapsing to a 68px rail.
 *
 * Collapse is a **visual** compaction only. The plan renders
 * `item.label.slice(0, 1)` in the rail, which is not a compact label but the
 * destruction of the accessible name — and the initials collide badly
 * (Categories/Connections, Reports/Review/Recurring), so it fails a sighted user
 * too. The labels stay in the DOM and are clipped by shell.css; the initial is a
 * separate, `aria-hidden` glyph, and `title` gives a pointer user the name back.
 */
type Props = {
  collapsed: boolean
  onToggle: () => void
  /** Hidden below `desktop`, where the rail is forced by the viewport and the
   *  control could not change anything. Defaults to shown so the component is
   *  usable on its own. */
  showToggle?: boolean
  /** Live counts from `useNavBadges`, keyed by path. The sidebar draws them; it does not fetch them. */
  badges?: NavBadges
}

/**
 * One nav item, wherever it is drawn — the scrolling groups or the pinned foot.
 *
 * Extracted in #192, and not for tidiness. `badge` went onto the shared
 * `NavItem` type, but only the group loop rendered it: `NAV_FOOT` is
 * `NavItem[]` too, so a badge on Settings would have type-checked, rendered
 * nothing, and reported no error. Two copies of this markup is how the two
 * loops disagreed about what a nav item is; one copy is what keeps them from
 * disagreeing again.
 *
 * Not exported. `Sidebar.badge.test.tsx` substitutes the route table rather
 * than reaching in here, so this stays an implementation detail of the file.
 */
function SidebarLink({ item, hitTarget }: { item: NavItem; hitTarget: number }) {
  return (
    <NavLink
      to={item.path}
      title={item.label}
      // `hoverable` supplies the 120ms timing and its reduced-motion kill
      // switch; shell.css supplies the state it transitions to. Dropping
      // either makes the other inert.
      className="sidebar__link hoverable"
      style={{ minHeight: hitTarget }}
    >
      {/* Decorative — the label carries the accessible name. #190 replaced
          `item.label.slice(0, 1)` here; the initials collided three ways and
          the 68px rail was a column of ambiguous letters. `nav.test.ts` pins
          that no two items share a glyph, which is the property the letters
          could not have. */}
      <Icon glyph={item.icon} />
      <span className="sidebar__label">{item.label}</span>
      {/* #192. Plain text inside the link, so the count lands in the accessible
          name without an aria-label restating the whole thing — a pill a
          screen-reader user cannot hear is a colour and a shape, which is the
          signal CLAUDE.md says may never be the only one. `!== undefined` and
          not a truthy test: a badge of 0 is a real answer ("nothing to review")
          and `&&` would render the number `0` as a bare text node besides. */}
      {item.badge !== undefined && (
        <>
          {/* The whitespace text node is load-bearing, not formatting. Without
              it the accessible-name algorithm concatenates the two spans with
              no separator and the link is announced as "Review12" — measured,
              and the first version of this shipped it. A whitespace-only node
              between flex items is not rendered as an anonymous flex item, so
              it costs no layout. */}
          {' '}
          <span className="sidebar__badge">{item.badge}</span>
        </>
      )}
    </NavLink>
  )
}

export function Sidebar({ collapsed, onToggle, showToggle = true, badges }: Props) {
  // M0-R29 — 44px below 640px, from the one definition of the policy. This was
  // a local `44 : 33`, a second implementation of the rule `Button` already
  // owned; web-architect blocked on it because #90 would then have had to be
  // applied twice. A media query in shell.css cannot do this job either: the
  // hook is the single breakpoint source CLAUDE.md names, and #90 measured an
  // inline height beating a query at 375px.
  const hitTarget = useHitTarget('page')

  return (
    /**
     * The dock, and the two cards inside it — #257.
     *
     * The design's Chrome part table gives the nav panel and the foot separate
     * cards: "Settings, support and the collapse toggle sit in a second
     * 18px-radius glass card BELOW the nav panel". They were one element with a
     * hairline between them, which is what elevation-is-the-hairline bought;
     * the panel now floats, so the separation is a gap.
     *
     * **Still one landmark.** #205's reasoning survives the split and is the
     * reason the dock is the `<nav>` rather than each card being one: two
     * navigation landmarks would both need distinct accessible names and would
     * be listed separately, which describes one sidebar worse than the visual
     * separation implies. The cards are presentational divs inside it.
     *
     * The rail modifier moved here with the foot. It has to sit on an ancestor
     * of BOTH cards, or the foot's links keep their labels while the panel's
     * lose them — the rule that hides a label is `.sidebar-dock--rail
     * .sidebar__label`, and the foot is no longer inside `.sidebar`.
     */
    <nav
      className={collapsed ? 'sidebar-dock sidebar-dock--rail' : 'sidebar-dock'}
      aria-label="Main"
    >
      <div className="sidebar">
      {/**
       * The brand block — #192.
       *
       * 62px because the top bar is 62px: the two hairlines meet at the
       * sidebar's right boundary and read as one line across the app. That is
       * also why `.sidebar` no longer carries its own padding — a 14px pad
       * stopped this rule 14px short of the boundary the top bar's reaches.
       *
       * `Link`, not `NavLink`. NavLink would set `aria-current="page"` here on
       * `/overview`, so that route would announce two current links — the
       * brand mark and the Overview nav item — for one location. The mark is a
       * home affordance, not a twenty-eighth destination.
       *
       * One link around both children rather than the design's two click
       * handlers: two adjacent targets to the same place is one more tab stop
       * and one more thing for a screen reader to read out, for no gain.
       *
       * The pill is `aria-hidden` for the same reason the nav icons are — "t5"
       * would otherwise join the accessible name and every announcement would
       * be "t5 trackE5".
       */}
      <Link to="/overview" className="sidebar__brand">
        <span className="sidebar__brand-pill" aria-hidden="true">
          t5
        </span>
        <span className="sidebar__brand-name">trackE5</span>
      </Link>

      <div className="sidebar__scroll">
      {NAV_GROUPS.map((group) => (
        <div key={group.name} className="sidebar__group">
          <span className="sidebar__group-name">{group.name}</span>
          {group.items.map((item) => (
            <SidebarLink key={item.path} item={withBadge(item, badges)} hitTarget={hitTarget} />
          ))}
        </div>
      ))}
        </div>
      </div>

      {/**
       * The foot — #205, moved out of the panel by #257.
       *
       * It is now the dock's SECOND card, not a block pinned inside the first:
       * the design's Chrome part table asks for an 18px-radius glass card below
       * the nav panel. `margin-top: auto` goes with the move — the gap between
       * two cards in a stretch-aligned column does that work now, and the
       * hairline that used to separate it from the scrolling list goes with the
       * hairline the panel itself lost. Settings and Help & support are single
       * links, not groups: Settings opens `SETTINGS_NAV` as a sub-navigation
       * column beside the content rather than expanding here.
       *
       * Inside the same `<nav>` rather than a second landmark. Two navigation
       * landmarks would both need distinct accessible names and would be
       * listed separately by a screen reader, which is a worse description of
       * one sidebar than the visual separation implies.
       */}
      <div className="sidebar__foot">
        {NAV_FOOT.map((item) => (
          <SidebarLink key={item.path} item={withBadge(item, badges)} hitTarget={hitTarget} />
        ))}

        {/**
         * The collapse control — #192, and its POSITION is the fix.
         *
         * It shipped as an outline button above the first group, where it was
         * the first thing in the navigation and the first thing after the skip
         * link. The design makes it the last element of the foot, below the
         * links and separated from the scrolling list by the foot's hairline.
         *
         * `variant="chip"` is the design's `--chip` fill, taken from the
         * primitive rather than restated here: `controlTokens.test.ts` bans
         * every stylesheet except `primitives.css` from addressing a button at
         * all — layout included, explicitly — so a `.sidebar__collapse` rule in
         * `shell.css` would be walking past that guard on a technicality rather
         * than honouring it. It stretches to the foot's full width on its own,
         * because the foot is a stretch-aligned flex column.
         *
         * `size="page"` for the 33px page hit target. FOUR divergences from the
         * design follow from taking the primitive whole, all deliberate and
         * listed so the next reader files none of them as a bug:
         *
         *   1. 33px, not the design's 34px — `HIT_TARGET.page`.
         *   2. The chip's 99px radius, not the design's 11px.
         *   3. `--control-ink` throughout, where the design rests the glyph at
         *      `--ink2` as a deliberate de-emphasis.
         *   4. `.btn--chip:hover` shifts the FILL to `--track`; the design
         *      shifts the INK to `--ink`.
         *
         * Each of 2–4 is a skin, and a skin belongs with the button. Recovering
         * them means a rule in `shell.css` addressing a primitive, which is what
         * `controlTokens.test.ts` bans — a caller-supplied class would slip past
         * the regex while walking past the rule the regex stands for.
         */}
        {showToggle && (
          <Button
            variant="chip"
            size="page"
            onClick={onToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            // The button controls its own container's width, so `aria-expanded`
            // belongs on it: a screen-reader user hears the state as well as the
            // action, rather than only being told which way it goes.
            aria-expanded={!collapsed}
          >
            <span aria-hidden="true">{collapsed ? '»' : '«'}</span>
          </Button>
        )}
      </div>
    </nav>
  )
}
